'use strict'

import { getCookie } from '@domql/utils'
import { opacify } from '@symbo.ls/scratch'

import AvenirLight from '@symbo.ls/fonts/49c5f3e1-5867-4b1d-a843-2d07fa60d85d.woff2'
import AvenirRegular from '@symbo.ls/fonts/2cd55546-ec00-4af9-aeca-4a3cd186da53.woff2'
import AvenirMedium from '@symbo.ls/fonts/627fbb5a-3bae-4cd9-b617-2f923e29d55e.woff2'
import AvenirDemiBold from '@symbo.ls/fonts/6afe4676-059a-4aa8-b891-29856bbcba22.woff2'
import AvenirBold from '@symbo.ls/fonts/14c73713-e4df-4dba-933b-057feeac8dd1.woff2'
import AvenirHeavy from '@symbo.ls/fonts/5c57b2e2-f641-421e-a95f-65fcb47e409a.woff2'

// const CDN_URL = 'https://fonts.gstatic.com/s/sourcecodepro/v21'

export const COLOR = {
  black: '#000000',
  white: '#ffffff',
  indigo: '#039E8D',
  purple: '#506EFF',
  yellow: '#E8CC56',
  pink: '#E51364',
  text: ['#47464E', '#7e7d86'],
  blue: '#3686F7',
  green: '#478d29',
  sky: '#13ABFC',
  wine: '#7136d1',

  dropdownDark: '#202020',

  grey25: '#473838',
  blue27: '#088177',
  blue94: '#ecf5f5',
  blue92: '#e6efee',

  gray75: '#c0c0c0',
  gray8: '#141414'
}

export const GRADIENT = {
  logoGradient: 'linear-gradient(56deg, rgba(0,191,255,1) 0%, rgba(127,58,236,1) 100%)',

  brandedGradient: {
    '@light': '--logoGradient',
    '@dark': '--logoGradient'
  },

  mapGradient: {
    '@light': 'radial-gradient(rgba(255,255,255,0) 35%, white 75%)',
    '@dark': 'radial-gradient(rgba(255,255,255,0) 35%, #141414 75%)'
  },

  packageGradient: {
    '@light': `linear-gradient(115deg, ${opacify('#13ABFC', '.12')} 0%, ${opacify('#13ABFC', '.065')} 50%, ${opacify('#13ABFC', '.12')} 100%)`,
    '@dark': `linear-gradient(115deg, ${opacify('#13ABFC', '.08')} 0%, ${opacify('#13ABFC', '.065')} 50%, ${opacify('#13ABFC', '.10')} 100%)`
  },

  researchGradient: {
    '@light': `linear-gradient(115deg, ${opacify('#7136d1', '.08')} 51%, ${opacify('#7136d1', '.05')} 87%, ${opacify('#7136d1', '.12')}  100%)`,
    '@dark': `linear-gradient(115deg, ${opacify('#7136d1', '.08')} 0%, ${opacify('#7136d1', '.065')} 50%, ${opacify('#7136d1', '.12')} 100%)`
  },

  researchGradientOverlay: {
    '@light': `linear-gradient(115deg, ${opacify('#7136d1', '.08')} 51%, ${opacify('#7136d1', '.5')} 87%, ${opacify('#7136d1', '.9')}  100%)`,
    '@dark': `linear-gradient(115deg, ${opacify('#7136d1', '.3')} 0%, ${opacify('#7136d1', '.2')} 50%, ${opacify('#2f1f4b', '.75')} 100%)`
  },

  packageGradientHover: {
    '@light': 'linear-gradient(135deg, #d6e9e9 46%, #d8ecec 87%, #dceeee 135%, #e0f0f0 100%)',
    '@dark': 'linear-gradient(115deg, rgba(236,245,245,0.05) 0%, rgba(239,247,247,0.05) 50%, rgba(243,249,249,0.05) 100%)'
  },

  bottomFadingShadow: {
    '@dark': 'linear-gradient(rgba(255, 255, 255, 0) 25%, #141414 85%)',
    '@light': 'linear-gradient(rgba(255, 255, 255, 0) 25%, rgba(255, 255, 255, 1)) 85%'
  }
}

export const THEME = {
  document: {
    '@dark': {
      background: 'gray8',
      color: 'white'
    },
    '@light': {
      background: 'white',
      color: 'black'
    }
  },

  none: {
    background: 'inherit',
    color: 'inherit'
  },

  branded: {
    '@dark': {
      color: 'white',
      background: 'brandedGradient'
    },
    '@light': {
      color: 'white',
      background: 'brandedGradient'
    }
  },

  primary: {
    '@dark': {
      color: 'white',
      background: 'blue .35',
      ':hover': {
        background: 'blue .45'
      }
    },
    '@light': {
      color: 'white',
      background: 'blue',
      ':hover': {
        background: 'blue 1 -15'
      }
    }
  },

  secondary: {
    '@dark': {
      color: 'white',
      background: 'white .065',
      ':hover': {
        background: 'white .15'
      }
    }
  },

  sepia: {
    ':hover': { opacity: '.65' },
    ':active': { opacity: '1' },

    '@dark': {
      color: 'yellow',
      ':hover': { background: 'yellow 0.15 85' },
      ':active': { background: 'yellow 0.15 85' }
    },
    '@light': {
      color: 'yellow 1',
      ':hover': { background: 'yellow 0.2' },
      ':active': { background: 'yellow 0.2' }
    }
  }
}

export const FONT = {
  Avenir: [{
    url: AvenirLight,
    fontWeight: 300
  }, {
    url: AvenirRegular,
    fontWeight: 400
  }, {
    url: AvenirMedium,
    fontWeight: 500
  }, {
    url: AvenirDemiBold,
    fontWeight: 600
  }, {
    url: AvenirBold,
    fontWeight: 700
  }, {
    url: AvenirHeavy,
    fontWeight: 900
  }]
}

export const FONT_FAMILY = {
  Default: {
    isDefault: true,
    value: ['"Avenir"'],
    type: 'serif'
  }
}

export const TYPOGRAPHY = {
  base: 16,
  default: 16,
  ratio: 1.125,
  subSequence: true,
  range: [-5, +18],

  templates: {
    h1: { fontWeight: '700' }
  },

  '@tabletL': {
    base: 15,
    ratio: 1.1
  }
}

export const SPACING = {
  range: [-5, +12]
}

export const ICONS = {
  arrow: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill-rule="evenodd" d="M21.4009822,3.43046745 C21.4009822,3.22337399 21.233133,3.05539228 21.0260396,3.05539228 L8.17775079,3.05539228 C7.97065732,3.05539228 7.80267562,3.22337399 7.8028082,3.43033487 L7.8028082,4.18022005 C7.8028082,4.38731352 7.97065732,4.55516264 8.17788337,4.55529522 L18.8400235,4.55529522 L2.81469096,20.49958 C2.66818758,20.6460834 2.66818758,20.8834056 2.81469096,21.029909 L3.34501994,21.5602379 C3.49139074,21.7066087 3.72884554,21.7067413 3.87534892,21.5602379 L19.9013444,5.61529027 L19.9012118,15.6683854 C19.9012118,15.8754789 20.0690609,16.043328 20.2761544,16.043328 L21.0260396,16.043328 C21.2330005,16.0434606 21.4009822,15.8754789 21.4009822,15.6683854 L21.4009822,3.43046745 Z"/></svg>',

  sunOutline: '<svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="m12.4545455 19.2727273c.250909 0 .4545454.2036363.4545454.4545454v1.8181818c0 .2509091-.2036364.4545455-.4545454.4545455h-.909091c-.250909 0-.4545454-.2036364-.4545454-.4545455v-1.8181818c0-.2509091.2036364-.4545454.4545454-.4545454zm-6.56138641-1.8086364.64295455.6427273c.1775.1775.1775.4652273 0 .6427273l-.64295455.6429545c-.1775.1775-.46522727.1775-.64272727 0l-.64295455-.6429545c-.1775-.1775-.1775-.4652273 0-.6427273l.64295455-.6427273c.1775-.1775.46522727-.1775.64272727 0zm12.85661361.0000227.6427273.6427273c.1775.1775.1775.4652273 0 .6427273l-.6427273.6429545c-.1777272.1772728-.4654545.1772728-.6429545 0l-.6427273-.6429545c-.1775-.1775-.1775-.4652273 0-.6427273l.6427273-.6427273c.1775-.1775.4652273-.1775.6429545 0zm-6.7496591-10.00956815c2.5063637 0 4.5454546 2.03931819 4.5454546 4.54545455 0 2.5063636-2.0390909 4.5454545-4.5454546 4.5454545-2.5063636 0-4.54545451-2.0390909-4.54545451-4.5454545 0-2.50613636 2.03909091-4.54545455 4.54545451-4.54545455zm0 1.81818182c-1.5038636 0-2.72727269 1.22363633-2.72727269 2.72727273 0 1.5038636 1.22340909 2.7272727 2.72727269 2.7272727 1.5038637 0 2.7272728-1.2234091 2.7272728-2.7272727 0-1.5036364-1.2234091-2.72727273-2.7272728-2.72727273zm-7.72738633 1.81818183c.25090909 0 .45454546.2036364.45454546.4545454v.909091c0 .250909-.20363637.4545454-.45454546.4545454h-1.81818182c-.25090909 0-.45454545-.2036364-.45454545-.4545454v-.909091c0-.250909.20363636-.4545454.45454545-.4545454zm17.27272723 0c.2509091 0 .4545455.2036364.4545455.4545454v.909091c0 .250909-.2036364.4545454-.4545455.4545454h-1.8181818c-.2509091 0-.4545454-.2036364-.4545454-.4545454v-.909091c0-.250909.2036363-.4545454.4545454-.4545454zm-15.65238632-6.48343183.64295455.64295455c.1775.1775.1775.46522727 0 .64272727l-.64295455.64272727c-.17727273.1775-.46522727.1775-.64272727 0l-.64272727-.64272727c-.17772728-.1775-.17772728-.46522727 0-.64272727l.64272727-.64295455c.1775-.17727272.46545454-.17727272.64272727 0zm12.85675002.00002273.6427273.64295455c.1775.1775.1775.46522727 0 .64272727l-.6427273.64272727c-.1777273.1775-.4654546.1775-.6429546 0l-.6427272-.64272727c-.1775-.1775-.1775-.46522727 0-.64272727l.6427272-.64295455c.1775-.1775.4652273-.1775.6429546 0zm-6.2952727-2.6075c.250909 0 .4545454.20363636.4545454.45454545v1.81818182c0 .25090909-.2036364.45454546-.4545454.45454546h-.909091c-.250909 0-.4545454-.20363637-.4545454-.45454546v-1.81818182c0-.25090909.2036364-.45454545.4545454-.45454545z" fill-rule="evenodd"/></svg>',
  sun: '<svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="m12.4914773 19.8636364c.2712954 0 .4914772.2201818.4914772.4914772v1.9659091c0 .2712955-.2201818.4914773-.4914772.4914773h-.9829546c-.2712954 0-.4914772-.2201818-.4914772-.4914773v-1.9659091c0-.2712954.2201818-.4914772.4914772-.4914772zm-7.09449903-1.9555881.6951946.6949489c.19167614.1919218.19167614.5030269 0 .6949488l-.6951946.6951946c-.19192188.1919219-.50302699.1919219-.69494887 0l-.6951946-.6951946c-.19192187-.1919219-.19192187-.503027 0-.6949488l.6951946-.6949489c.19192188-.1919219.50302699-.1919219.69494887 0zm13.90109063.0000246.6949489.6949488c.1921676.1919219.1921676.503027 0 .6949489l-.6949489.6951946c-.1919219.1916761-.5032727.1916761-.6949489 0l-.6951946-.6951946c-.1919218-.1919219-.1919218-.503027 0-.6949489l.6951946-.6949488c.1916762-.1919219.503027-.1919219.6949489 0zm-7.2980689-10.82284563c2.7100057 0 4.9147727 2.20501279 4.9147727 4.91477273 0 2.7100057-2.204767 4.9147727-4.9147727 4.9147727-2.71000568 0-4.91477273-2.204767-4.91477273-4.9147727 0-2.70975994 2.20476705-4.91477273 4.91477273-4.91477273zm-8.35511364 3.93181823c.27129546 0 .49147728.2201818.49147728.4914772v.9829546c0 .2712954-.22018182.4914772-.49147728.4914772h-1.96590909c-.27129545 0-.49147727-.2201818-.49147727-.4914772v-.9829546c0-.2712954.22018182-.4914772.49147727-.4914772zm18.67613634 0c.2712955 0 .4914773.2201818.4914773.4914772v.9829546c0 .2712954-.2201818.4914772-.4914773.4914772h-1.9659091c-.2712954 0-.4914772-.2201818-.4914772-.4914772v-.9829546c0-.2712954.2201818-.4914772.4914772-.4914772zm-16.92414273-7.0102107.6951946.6951946c.19192188.19192188.19192188.50302699 0 .69494887l-.6951946.69494886c-.19192187.19192188-.50302699.19192188-.69494886 0l-.69494887-.69494886c-.19216761-.19192188-.19216761-.50302699 0-.69494887l.69494887-.6951946c.19192187-.19167614.50302699-.19167614.69494886 0zm13.90109063.00002458.6951946.6951946c.1919219.19192187.1919219.50302699 0 .69494886l-.6951946.69494886c-.1919219.19192188-.503027.19192188-.6949489 0l-.6951946-.69494886c-.1916761-.19192187-.1916761-.50302699 0-.69494886l.6951946-.6951946c.1919219-.19192188.503027-.19192188.6949489 0zm-6.8064933-2.81935938c.2712954 0 .4914772.22018182.4914772.49147727v1.96590909c0 .27129546-.2201818.49147728-.4914772.49147728h-.9829546c-.2712954 0-.4914772-.22018182-.4914772-.49147728v-1.96590909c0-.27129545.2201818-.49147727.4914772-.49147727z" fill-rule="evenodd"/></svg>',

  moonOutline: '<svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="m9.05308974 3.53489233c.34949825 0 .61724691.37649811.44349778.72649636-1.41799291 2.85798571-.93774531 6.41846791 1.44299278 8.79920601 1.4919925 1.4917425 3.4467328 2.2374888 5.401723 2.2374888 1.1652442 0 2.3304883-.2649987 3.397733-.794496.0752496-.0372498.1519992-.0542497.2259989-.0542497.3244984 0 .597247.3247483.4697476.6707466-.4354978 1.1812441-1.1267443 2.2897386-2.0754896 3.2387338-1.7562412 1.7559912-4.0572297 2.6337368-6.3589682 2.6337368-2.49323753 0-4.98697506-1.0307448-6.78646607-3.0914845-2.97198514-3.403483-2.9189854-8.58270709.11474943-11.93119035 1.01274494-1.11774441 2.23423883-1.91799041 3.54623227-2.40223799.0604997-.02249989.1204994-.03274983.17824911-.03274983zm-2.35148825 3.90123049c-2.22948885 2.58548708-2.23223883 6.57146718.01874991 9.14945428 1.33574332 1.5297423 3.2602337 2.4069879 5.2799736 2.4069879 1.6952415 0 3.2984835-.598997 4.5677272-1.6972415-.0754997.00175-.1509993.00275-.2267489.00275-2.5747371 0-4.995225-1.002745-6.81571593-2.8232359-1.90724046-1.9072404-2.88298558-4.4632277-2.82398588-7.03871478zm9.99610001-2.31241344c.1672492-.16699916.4382478-.16699916.605247 0l1.5734921 1.57374213c.1672492.16699917.1672492.43799781 0 .60499698l-1.5734921 1.57374213c-.1669992.16699916-.4379978.16699916-.605247 0l-1.5734921-1.57374213c-.1669992-.16699917-.1669992-.43799781 0-.60499698z" fill-rule="evenodd"/></svg>',
  moon: '<svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="m8.87455781 3.56762108c.42724894-.1577496.82424794.28599929.62199845.69374827-1.41824646 2.85799285-.93774766 6.41848395 1.44299644 8.79922795 2.380744 2.3809941 5.9412351 2.8612429 8.799228 1.4429964.4054989-.2012495.8522478.1917496.6957482.6164985-.4354989 1.181497-1.1264972 2.2897443-2.0754948 3.2387419-3.6584909 3.6582409-9.68472579 3.5059912-13.14546713-.4577489-2.97174257-3.4034914-2.91899271-8.5827285.11474971-11.93122012 1.01274747-1.11774721 2.23424441-1.91774521 3.54624113-2.402244zm7.82293049 1.55609611c.1672495-.16699958.4379989-.16699958.6052484 0l1.5734961 1.57374607c.1672496.16699958.1672496.4379989 0 .60499848l-1.5734961 1.57374607c-.1672495.16699958-.4379989.16699958-.6052484 0l-1.5734961-1.57374607c-.1669996-.16699958-.1669996-.4379989 0-.60499848z" fill-rule="evenodd"/></svg>'
}

export const ANIMATION = {
  fadeInUp: {
    from: {
      transform: 'translate3d(0, 12.5%, 1px)',
      opacity: 0
    },
    to: {
      transform: 'translate3d(0, 0, 1px)',
      opacity: 1
    }
  },
  fadeOutDown: {
    from: {
      transform: 'translate3d(0, 0, 1px)',
      opacity: 1
    },
    to: {
      transform: 'translate3d(0, 12.5%, 1px)',
      opacity: 0
    }
  },
  marquee: {
    from: {
      transform: 'translate3d(0, 0, 1px)'
    },
    to: {
      transform: 'translate3d(-50%, 0, 1px)'
    }
  }
}

export const TIMING = {
  default: 150,
  base: 150,
  subSequence: true,
  defaultBezier: 'cubic-bezier(.29, .67, .51, .97)'
}

export const SVG = {
  logo: '<svg xmlns="http://www.w3.org/2000/svg" width="52" height="36" viewBox="0 0 52 36"><defs><linearGradient id="a" x1="25.188%" x2="50%" y1="75.435%" y2="24.565%"><stop offset="0%" stop-color="#00BFFF"/><stop offset="100%" stop-color="#7F3AEC"/></linearGradient></defs><path fill="url(#a)" fill-rule="evenodd" d="M29.8314753,61.1591958 C31.0465906,62.3185635 32.9714894,62.2733712 34.1308571,61.0582559 L34.2198406,60.9607925 L36.9012977,57.8750763 C37.8200493,56.8077042 38.422624,56.0910352 38.7090217,55.7250692 C41.8145806,55.4454025 48.5844184,51.5124251 51.5273577,48.4557707 C56.1558155,43.6484695 56.4113171,37.3980561 60.9156739,33.6144634 C63.8295406,31.1668588 68.7818894,30.1630543 75.7727204,30.6030498 C76.0707801,30.621784 76.3321925,30.4226066 76.4014266,30.1426109 L76.4192787,30.0149949 L76.4192787,29.9958791 C76.4192787,29.6360403 76.1275717,29.3443333 75.7677329,29.3443298 L60.9156739,29.3443298 C57.8084016,27.9160761 56.4524858,26 54.0028133,26 C53.1284467,26.0924567 52.0325986,26.6708732 50.715269,27.7352493 C49.0666955,29.0672792 48.8100727,31.4835343 50.1420934,33.1321152 L50.224245,33.2303892 L51.2571649,34.3504577 C52.1014022,35.2633509 52.1102907,36.6545635 51.3127113,37.5766765 L51.1537214,37.7438442 L42.1998245,28.2037603 C41.040598,26.9686424 39.15674,26.7564595 37.7518145,27.7027715 C36.6030195,28.4765622 36.2990186,30.0351259 37.0728094,31.1839209 C37.1465206,31.2933549 37.2287529,31.3968014 37.3187405,31.4932968 L41.7236397,36.2167542 C42.1198833,36.6416538 42.1074622,37.3043219 41.6955739,37.7140739 C41.3399329,38.0678702 40.7867674,38.101904 40.3931156,37.8171036 L40.2457581,37.6869658 L31.6683291,28.4909377 C30.4944915,27.2324437 28.6025787,26.9614348 27.1226465,27.839787 C25.9803978,28.5177212 25.6039965,29.9932709 26.2819307,31.1355196 C26.3646994,31.2749762 26.4613607,31.4057039 26.570439,31.5257069 L40.9670327,47.3641905 C41.3983266,47.8386807 41.3949312,48.5642604 40.9592153,49.0346933 C40.5327403,49.4951489 39.8148053,49.525713 39.3507392,49.1031696 L37.8353969,47.723414 L29.678716,56.6308792 C28.4887746,57.9303492 28.5566655,59.9428723 29.8314753,61.1591958 Z" transform="translate(-25 -26)"/></svg>'
}

const RESET = {
  html: {
    overflow: 'hidden auto'
  }
}

export default {
  globalTheme: getCookie('globalTheme') || 'dark',
  COLOR,
  GRADIENT,
  THEME,
  FONT,
  FONT_FAMILY,
  TYPOGRAPHY,
  SPACING,
  ICONS,
  ANIMATION,
  TIMING,
  SVG_DATA: SVG,
  RESET,
  MEDIA: {
    '>mobileL': '(min-width: 768px)'
  },
  CASES: {}
}
